import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import DispatchManagerContactsModal from "../DispatchManagerContactsModal";
import DispatchManagerMediasModal from "../DispatchManagerMediasModal";
import DispatchManagerMessagesModal from "../DispatchManagerMessagesModal";
import formatDefaultValueForDatetimeInput from "../../utils/datetime/formatDefaultValueForDatetimeInputs";
import toastError from "../../errors/toastError";
import useAllWhatsAppsDispatch from "../../hooks/useAllWhatsAppsDispatch";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },

  fieldWithPersonalizedIcon: {
    "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
  },

  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "1em",
  },

  btnWrapper: { position: "relative", },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  floatingButton: {
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },
}));

const DispatchManagerModal = ({ open, onClose, dispatchId, handleOpenLoadingDispatchModal, handleCloseLoadingDispatchModal }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const { loading, whatsapps } = useAllWhatsAppsDispatch();

  const [selectedTheme, setSelectedTheme] = useState(localStorage.getItem("theme"));

  const initialState = {
    name: "",
    datetimeToDispatch: formatDefaultValueForDatetimeInput(new Date(new Date().getTime() + 60 * 60 * 2 * 1000)), // 120 minutes in the future in milliseconds
    recurrence: "once",
    whatsappId: "",
    dispatchContacts: [],
    additionalContacts: [],
    dispatchMediaPaths: [],
    dispatchMessages: { message1: "", message2: "", message3: "" }
  };
  
  const [dispatch, setDispatch] = useState(initialState);
  
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [messagesModalOpen, setMessagesModalOpen] = useState(false);
  const [mediasModalOpen, setMediasModalOpen] = useState(false);


  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    const fetchMedia = async (mediaUrl) => {
      const { data, headers } = await api.get(mediaUrl, { responseType: "blob", });

      const contentType = headers["content-type"] || "application/octet-stream";
      const blob = new Blob([data], { type: contentType });

      const fileName = mediaUrl.split("/dispatchMedias/")[1];
      const file = new File([blob], fileName, { type: contentType });

      return file;
    };

    const fetchDispatch = async () => {
      if (!open) return;
      if (!dispatchId) return;

      try {
        // ***---- Fetching Dispatch Data ----***
        const { data } = await api.get(`/dispatch/${dispatchId}`);

        // ***---- Transforming 'datetimeToDispatch' and 'whatsappId' ----***
        data.datetimeToDispatch = formatDefaultValueForDatetimeInput(new Date(data.datetimeToDispatch));
        data.whatsappId = whatsapps.find(whatsapp => whatsapp.id === data.whatsappId) ? data.whatsappId : "";

        // ***---- Transforming 'dispatchContacts' ----***
        const dispatchContacts = [...data.dispatchContacts];

        data.dispatchContacts = dispatchContacts
          .filter(dispatchContact => ![null, undefined].includes(dispatchContact.contactId))
          .map(dispatchContact => dispatchContact.contactId);

        data.additionalContacts = dispatchContacts
          .filter(dispatchContact => ![null, undefined].includes(dispatchContact.additionalContact))
          .map(dispatchContact => dispatchContact.additionalContact);

        // ***---- Transfoming 'dispatchMessages' ----***
        const dispatchMessages = { message1: "", message2: "", message3: "" };
        data.dispatchMessages.forEach((value, index) => dispatchMessages[`message${index+1}`] = value.body);
        data.dispatchMessages = dispatchMessages;

        // ***---- Transforming 'dispatchMediaPaths ----***
        const mediaPaths = data.dispatchMediaPaths.map(mediaPath => mediaPath.mediaPath);

        data.dispatchMediaPaths = await Promise.all(
          mediaPaths.map(async (mediaPath) => {
            const fetchedMedia = await fetchMedia(mediaPath);
            return fetchedMedia;
          })
        );

        // ***---- Setting State ----***
        setDispatch(previousValue => { return { ...previousValue, ...data }; });
      } catch (exception) {
        console.log("DispatchManagerModal Use Effect 1 Exception:", exception);
        toastError(exception);
      }
    };

    fetchDispatch();

    setSelectedTheme(localStorage.getItem("theme"));
  }, [open, dispatchId, whatsapps]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
    setTimeout(() => setDispatch(initialState), 100);
  };

  const handleOpenContactsModal = () => {
    setContactsModalOpen(true);
  };

  const handleCloseContactsModal = () => {
    setContactsModalOpen(false);
  };

  const handleOpenMessagesModal = () => {
    setMessagesModalOpen(true);
  };

  const handleCloseMessagesModal = () => {
    setMessagesModalOpen(false);
  };

  const handleOpenMediasModal = () => {
    setMediasModalOpen(true);
  };

  const handleCloseMediasModal = () => {
    setMediasModalOpen(false);
  };

  const validateDispatchValues = (values) => {
    let areValuesValid = true;

    // ***---- Name ----***
    if (values.name.length < 3 || values.name.length > 50) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.nameSize"));
    }

    // ***---- Datetime to Dispatch ----***
    if (values.datetimeToDispatch === "") {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.datetimeToDispatchEmpty"));
    }

    const currentDatetimePlusOneHour = new Date(new Date().getTime() + 60 * 60 * 1000); // 60 minutes in the future in milliseconds
    if (new Date(values.datetimeToDispatch) <= currentDatetimePlusOneHour) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.datetimeToDispatchInvalid"));
    }
    
    // ***---- Recurrence ----***
    if (!["once", "weekly", "monthly"].includes(values.recurrence)) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.recurrenceInvalid"));
    }

    // ***---- Connection ----***
    if ([null, undefined].includes(values.whatsappId)) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.connectionInvalid"));
    }

    // ***---- Messages ----***
    const isMessage1Invalid = values.dispatchMessages.message1.length < 3 || values.dispatchMessages.message1.length > 500;
    const isMessage2Invalid = values.dispatchMessages.message2 !== "" && (values.dispatchMessages.message2.length < 3 || values.dispatchMessages.message2.length > 500);
    const isMessage3Invalid = values.dispatchMessages.message3 !== "" && (values.dispatchMessages.message3.length < 3 || values.dispatchMessages.message3.length > 500);
    const isAnyMessageInvalid = isMessage2Invalid || isMessage3Invalid;

    if (isMessage1Invalid) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.message1Size"));
      setMessagesModalOpen(true);
    }

    if (isAnyMessageInvalid) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.otherMessagesSize"));
      setMessagesModalOpen(true);
    }

    // ***---- Messages && Medias ----***
    const isRequiredContentEmpty = [null, undefined, ""].includes(values.dispatchMessages.message1) && values.dispatchMediaPaths.length === 0;

    if (isRequiredContentEmpty) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.requiredContentEmpty"));
      setMessagesModalOpen(true);
    }

    // ***---- Contacts ----***
    const areContactsInvalid = values.dispatchContacts.length < 3 && values.additionalContacts.length < 3;

    if (areContactsInvalid) {
      areValuesValid = false;
      toast.info(i18n.t("dispatchManagerModal.validations.contactsSize"));
      setContactsModalOpen(true);
    }

    // Return
    return areValuesValid;
  };

  const handleSaveDispatch = async (values) => {
    const isDispatchValuesValid = validateDispatchValues(values);
    if (!isDispatchValuesValid) return;

    try {
      values.datetimeToDispatch = new Date(values.datetimeToDispatch).toISOString();

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("datetimeToDispatch", values.datetimeToDispatch);
      formData.append("recurrence", values.recurrence);
      formData.append("whatsappId", values.whatsappId);
      values.dispatchContacts.forEach(dispatchContact => formData.append("dispatchContacts", dispatchContact));
      values.additionalContacts.forEach(additionalContact => formData.append("additionalContacts", additionalContact));
      values.dispatchMediaPaths.forEach(media => formData.append("medias", media));
      Object.values(values.dispatchMessages).forEach(dispatchMessage => formData.append("dispatchMessages", dispatchMessage));

      const loadingModalTitleText = dispatchId ? "updating" : "creating";
      handleOpenLoadingDispatchModal(loadingModalTitleText);

      if (dispatchId) await api.put(`/dispatch/${dispatchId}`, formData);
      else await api.post("/dispatch", formData);

      handleCloseLoadingDispatchModal();
      handleClose();
    } catch (exception) {
      handleCloseLoadingDispatchModal();

      console.log("HandleSaveDispatch Exception:", exception);
      toastError(exception);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" scroll="paper" fullWidth>
        <DialogTitle>
          {dispatchId ? i18n.t("dispatchManagerModal.title.edit") : i18n.t("dispatchManagerModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={dispatch}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(async () => {
              await handleSaveDispatch(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                {/* 
                  ************
                  ** Modals **
                  ************
                */}
                <DispatchManagerContactsModal open={contactsModalOpen} onClose={handleCloseContactsModal} whatsappId={values.whatsappId} />
                <DispatchManagerMessagesModal open={messagesModalOpen} onClose={handleCloseMessagesModal} />
                <DispatchManagerMediasModal open={mediasModalOpen} onClose={handleCloseMediasModal} />

                {/* 
                  **********
                  ** Name **
                  **********
                */}
                <Field
                  as={TextField}
                  label={i18n.t("dispatchManagerModal.form.name")}
                  autoFocus
                  autoComplete="off"
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  InputProps={{ inputProps: { minLength: 3, maxLength: 50 } }}
                />

                {/* 
                  **************************
                  ** Datetime To Dispatch **
                  **************************
                */}
                <Field
                  as={TextField}
                  type="datetime-local"
                  label={i18n.t("dispatchManagerModal.form.datetimeToDispatch")}
                  autoComplete="off"
                  name="datetimeToDispatch"
                  error={touched.datetimeToDispatch && Boolean(errors.datetimeToDispatch)}
                  helperText={touched.datetimeToDispatch && errors.datetimeToDispatch}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  style={{ "color-scheme": selectedTheme, }}
                />

                <br />
                <br />
                <Divider />
                <Divider />

                {/* 
                  ****************
                  ** Connection **
                  ****************
                */}
                {!loading && (
                  <>
                    <br />
                    <FormControl variant="outlined" margin="dense" required fullWidth>
                      <InputLabel>{i18n.t("dispatchManagerModal.form.connection")}</InputLabel>

                      <Field
                        as={Select}
                        label={i18n.t("dispatchManagerModal.form.connection")}
                        name="whatsappId"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        required
                        className={classes.fieldWithPersonalizedIcon}
                      >
                        <MenuItem value={''}>&nbsp;</MenuItem>
                        {whatsapps.map(whatsapp => (
                          <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </>
                )}

                {/* 
                  *************************
                  ** Contacts & Messages **
                  *************************
                */}
                <br />
                <br />
                <Divider />
                <br />

                <div className={classes.buttonsContainer}>
                  <Button
                    onClick={handleOpenContactsModal}
                    color="inherit"
                    disabled={isSubmitting || !values.whatsappId}
                    variant="outlined"
                    className={classes.floatingButton}
                  >
                    {i18n.t("dispatchManagerModal.form.buttons.contacts")}
                  </Button>

                  <Button
                    onClick={handleOpenMessagesModal}
                    color="inherit"
                    disabled={isSubmitting}
                    variant="outlined"
                    className={classes.floatingButton}
                  >
                    {i18n.t("dispatchManagerModal.form.buttons.messages")}
                  </Button>

                  <Button
                    onClick={handleOpenMediasModal}
                    color="inherit"
                    disabled={isSubmitting}
                    variant="outlined"
                    className={classes.floatingButton}
                  >
                    {i18n.t("dispatchManagerModal.form.buttons.medias")}
                  </Button>
                </div>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="inherit"
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.floatingButton}
                >
                  {i18n.t("dispatchManagerModal.buttons.cancel")}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={`${classes.btnWrapper} ${classes.floatingButton}`}
                >
                  {dispatchId ? i18n.t("dispatchManagerModal.buttons.okEdit") : i18n.t("dispatchManagerModal.buttons.okAdd")}
                  {isSubmitting && (<CircularProgress size={24} className={classes.buttonProgress} />)}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default DispatchManagerModal;
